/**
 * Centralized API Instance
 *
 * Provides a single point for all API calls with:
 * - Automatic token injection
 * - Token expiry checking
 * - Centralized error handling
 * - 401 auto-redirect
 */

import { getAuthHeader, removeToken, getToken } from '../utils/auth'
import { isTokenExpired } from '../utils/tokenCheck'
import { API_URL } from '../config/env'
import { PUBLIC_ROUTES } from '../config/constants'
import type { ApiResponse } from '../types/common'

/**
 * Handle 401 Unauthorized responses by clearing auth and redirecting to login
 */
const handleUnauthorized = () => {
  removeToken()
  const currentPath = window.location.pathname

  if (!PUBLIC_ROUTES.includes(currentPath as (typeof PUBLIC_ROUTES)[number])) {
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
  }
}

/**
 * Centralized error handler for API responses
 */
export const handleApiError = async (
  response: Response,
  defaultMessage = 'An error occurred'
): Promise<string> => {
  if (response.status === 401) {
    handleUnauthorized()
    return 'Session expired. Please log in again.'
  }

  try {
    const errorData = await response.json()
    return errorData.message || errorData.error || defaultMessage
  } catch {
    return `${defaultMessage}: ${response.status} ${response.statusText}`
  }
}

/**
 * Makes an authenticated API request with automatic token injection.
 *
 * This function:
 * - Checks token expiry before making request
 * - Automatically adds Authorization header if token exists
 * - Sets Content-Type to application/json
 * - Handles base URL configuration
 * - Automatically handles 401 responses by redirecting to login
 *
 * @param endpoint - API endpoint path (e.g., '/auth/login')
 * @param options - Fetch API options (method, body, headers, etc.)
 * @returns Promise resolving to Response object
 */
export const apiRequest = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  // Check token expiry before making request
  const token = getToken()

  if (token) {
    const expired = isTokenExpired(token)

    if (expired) {
      handleUnauthorized()
      return new Response(
        JSON.stringify({
          success: false,
          message: 'Token expired',
        }),
        {
          status: 401,
          statusText: 'Unauthorized',
          headers: { 'Content-Type': 'application/json' },
        }
      )
    }
  }

  const authHeader = getAuthHeader()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  }

  // Add Authorization header if token exists
  if (authHeader) {
    headers['Authorization'] = authHeader
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  })

  // Handle 401 Unauthorized - token expired or invalid
  if (response.status === 401) {
    handleUnauthorized()
  }

  return response
}

/**
 * Makes an authenticated GET request.
 *
 * @param endpoint - API endpoint path
 * @param options - Additional fetch options
 * @returns Promise resolving to Response object
 */
export const apiGet = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  return apiRequest(endpoint, {
    ...options,
    method: 'GET',
  })
}

/**
 * Makes an authenticated POST request with JSON body.
 *
 * @param endpoint - API endpoint path
 * @param body - Request body object (will be JSON stringified)
 * @param options - Additional fetch options
 * @returns Promise resolving to Response object
 */
export const apiPost = async <T = unknown>(
  endpoint: string,
  body?: T,
  options: RequestInit = {}
): Promise<Response> => {
  return apiRequest(endpoint, {
    ...options,
    method: 'POST',
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
}

/**
 * Makes an authenticated PUT request with JSON body.
 *
 * @param endpoint - API endpoint path
 * @param body - Request body object (will be JSON stringified)
 * @param options - Additional fetch options
 * @returns Promise resolving to Response object
 */
export const apiPut = async <T = unknown>(
  endpoint: string,
  body?: T,
  options: RequestInit = {}
): Promise<Response> => {
  return apiRequest(endpoint, {
    ...options,
    method: 'PUT',
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
}

/**
 * Makes an authenticated DELETE request.
 *
 * @param endpoint - API endpoint path
 * @param options - Additional fetch options
 * @returns Promise resolving to Response object
 */
export const apiDelete = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  return apiRequest(endpoint, {
    ...options,
    method: 'DELETE',
  })
}

/**
 * Helper function to parse and type-check API responses.
 *
 * @param response - Fetch Response object
 * @returns Promise resolving to typed API response
 */
export async function parseApiResponse<T>(
  response: Response
): Promise<ApiResponse<T>> {
  const data = await response.json()
  return data as ApiResponse<T>
}

/**
 * Makes an API request and automatically parses the response.
 * Throws an error if the response is not successful.
 *
 * @param endpoint - API endpoint path
 * @param options - Fetch API options
 * @returns Promise resolving to typed API response data
 */
export async function apiRequestWithParsing<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await apiRequest(endpoint, options)
  const result = await parseApiResponse<T>(response)

  if (!response.ok || !result.success) {
    const errorMessage = 'message' in result ? result.message : 'Request failed'
    throw new Error(errorMessage)
  }

  return result.data
}
